import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ServiceCard, SectionHeading, StatsSection, TestimonialsSection } from "@/components/Cards";
import { Search, CalendarCheck, CreditCard, PartyPopper, Quote } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1666694051761-cd972857da30?crop=entropy&cs=srgb&fm=jpg&q=85";
const STEPS = [
  { icon: Search, t: "Discover", d: "Choose a Mandir and a worship or ritual" },
  { icon: CalendarCheck, t: "Schedule", d: "Pick date, time, purohit & plan" },
  { icon: CreditCard, t: "Pay Securely", d: "Complete payment via Razorpay" },
  { icon: PartyPopper, t: "Receive Blessings", d: "Get confirmation & prasad" },
];

export default function DivineBless() {
  const [worships, setWorships] = useState([]);
  const [rituals, setRituals] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [qi, setQi] = useState(0);

  useEffect(() => {
    api.get("/services", { params: { type: "worship" } }).then((r) => setWorships(r.data));
    api.get("/services", { params: { type: "ritual" } }).then((r) => setRituals(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data.slice(0, 4)));
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/quotes").then((r) => setQuotes(r.data));
  }, []);

  useEffect(() => {
    if (quotes.length < 2) return;
    const t = setInterval(() => setQi((i) => (i + 1) % quotes.length), 5000);
    return () => clearInterval(t);
  }, [quotes]);

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0"><img src={HERO} alt="" className="h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-r from-background via-background/92 to-background/50" /></div>
        <div className="relative px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 lg:py-24">
          <p className="font-devanagari text-accent text-lg mb-2">॥ आशीर्वाद ॥</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight max-w-2xl leading-tight">Receive Divine Blessings</h1>
          <p className="mt-4 text-muted-foreground max-w-xl text-lg">Book authentic Worships and Vedic Rituals, performed with complete vidhi by verified Purohits.</p>
          {quotes.length > 0 && (
            <div data-testid="quote-slider" className="mt-8 max-w-xl rounded-2xl border border-accent/30 bg-card/70 backdrop-blur-sm p-6 relative overflow-hidden">
              <Quote className="absolute -top-2 -left-1 h-16 w-16 text-accent/10" />
              <p key={qi} className="font-serif text-lg lg:text-xl italic leading-relaxed relative animate-float-up" data-testid="quote-text">"{quotes[qi].text}"</p>
              {quotes[qi].author && <p className="text-sm text-accent font-medium mt-3 relative" data-testid="quote-author">— {quotes[qi].author}</p>}
              <div className="flex gap-1.5 mt-4 relative">
                {quotes.map((_, i) => (
                  <button key={i} data-testid={`quote-dot-${i}`} onClick={() => setQi(i)} aria-label={`Quote ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all ${i === qi ? "w-6 bg-accent" : "w-1.5 bg-muted-foreground/40 hover:bg-muted-foreground/70"}`} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20">
        <Tabs defaultValue="worship" className="w-full" data-testid="divine-bless-tabs">
          <div className="flex justify-center mb-10">
            <TabsList className="rounded-full h-12 p-1 bg-secondary">
              <TabsTrigger value="worship" data-testid="tab-worship" className="rounded-full px-6 h-10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Book Worship</TabsTrigger>
              <TabsTrigger value="ritual" data-testid="tab-ritual" className="rounded-full px-6 h-10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Book Vedic Rituals</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="worship">
            <SectionHeading eyebrow="Puja & Archana" title="Featured Worships" subtitle="Invoke the divine with time-honoured pujas at India's most sacred temples." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{worships.map((s) => <ServiceCard key={s.id} s={s} />)}</div>
          </TabsContent>
          <TabsContent value="ritual">
            <SectionHeading eyebrow="Sanskar & Havan" title="Featured Vedic Rituals" subtitle="Sacred sanskars and havans for every milestone of life." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{rituals.map((s) => <ServiceCard key={s.id} s={s} />)}</div>
          </TabsContent>
        </Tabs>
      </section>

      <section className="bg-secondary/40 border-y border-border">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20">
          <SectionHeading eyebrow="Simple & Sacred" title="How booking works" center />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.t} className="rounded-2xl bg-card border border-border p-6 text-center relative">
                <span className="absolute top-4 right-4 font-serif text-3xl font-bold text-accent/20">{i + 1}</span>
                <span className="h-12 w-12 mx-auto grid place-items-center rounded-xl bg-gradient-to-br from-accent/20 to-primary/20 text-primary mb-4"><s.icon className="h-6 w-6" /></span>
                <h3 className="font-serif text-lg font-semibold">{s.t}</h3>
                <p className="text-sm text-muted-foreground mt-1.5">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <StatsSection stats={stats} />

      <TestimonialsSection testimonials={testimonials} title="Loved by devotees" />
    </div>
  );
}
